import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListJoorOrdersInput } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the orders of Joor (pattern.md §10): the latest state of each, the ones that wait for the Customer
// Service, and what applying a change that waits would do (ADR-124). Under the key of the sales orders, so whatever
// writes an order reloads them too: a state applied changes its sales order, and the other way round.

const orders = api.sales["joor-orders"];

type ListPage = InferResponseType<typeof orders.$get, 200>;
export type JoorOrder = ListPage["items"][number];
export type JoorChange = InferResponseType<(typeof orders)[":joorOrderId"]["change"]["$get"], 200>;

/** The filters of the list input (`listJoorOrdersInput`), without the paging the queries add. */
export type JoorOrderFilters = Pick<ListJoorOrdersInput, "reason" | "q" | "salesOrder"> & {
  waiting?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: ["sales-orders", "joor"] as const,
  lists: ["sales-orders", "joor", "list"] as const,
};

export const joorOrderQueries = {
  ...keys,

  list: (filters: JoorOrderFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The order of Joor a sales order is, with what waits of it: what its page decides (B-31). */
  ofSalesOrder: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of", salesOrderId],
      queryFn: async ({ signal }) => {
        const page = await unwrap(orders.$get({ query: { salesOrder: salesOrderId } }, { init: { signal } }));
        return page.items[0] ?? null;
      },
    }),

  /** What applying the change that waits would do: read again whenever the order or Joor moves. */
  change: (joorOrderId: string) =>
    queryOptions({
      queryKey: [...keys.all, "change", joorOrderId],
      queryFn: ({ signal }) =>
        unwrap(orders[":joorOrderId"].change.$get({ param: { joorOrderId } }, { init: { signal } })),
    }),
};
